const View = require('public_framework/view')
const GoogleMap = require('shared/javascripts/public_framework/services/google_map')
const THEMES = require('public_shared_components/ui/map/styles')

// Need to be in sync with .sass version
// See front/apps/public/shared/styles/themes/_default.sass
const BREAKPOINTS = {
  tablet: 524,
  tablet_l: 668,
  desktop_s: 896,
  desktop_m: 1142
}

/**
 * This is a common component for drawing a map and showing some locations on it.
 * It is possible to:
 * - Draw one or many markers.
 * - Remove all markers.
 * - Set new markers.
 *
 * Options:
 * - `data-min-screen` the min screen width when map should be loaded (if you don't need it on mobile, for example).
 * - `data-map-zoom` set the zoom, omitted if more than one location is specified, default zoom is 5.
 * - `data-style` only available value is `silver`, if not specified - default Google map styles are used.
 * - `data-marker-path` a URL of the image used as a marker.
 * - `data-locations` an array of location objects of the shape {latitude, longitude, address}.
 *   If only address is specified, the map will try to find coordinates using it.
 *
 * Events:
 * - `map:add_markers` (payload: locations array) adds markers to the map.
 * - `map:remove_markers` (payload: none) removes all the markers from the map.
 * - `map:activate_marker` (payload: location) marks marker for selected location as active (You can style it).
 * - `map:deactivate_marker` (payload: location) marks marker for selected location as inactive.
 *
 * Example:
 *   <div data-view='ui#map'
 *        data-min-screen='desktop_s'
 *        data-style='silver'
 *        data-marker-path='/public/marker.png'
 *        data-locations='[{"latitude":"37.9706072", "longitude":"23.7405495", "address":"Vasileos Konstantinou 5, Athens, Greece"}]'>
 *   </div>
 *
 * Note:
 *   The only available option to style markers is by setting a title and then using CSS selectors like [title='Title Text']
 *   Google does not provide any other API for doing this, however this seems to be a reliable way.
 */

const MAP_OPTIONS = {
  scrollwheel: false,
  streetViewControl: false,
  mapTypeControl: false,
  fullscreenControl: false
}

const MARKER_WIDTH = 14
const MARKER_HEIGHT = 14
const DEFAULT_ZOOM = 5

const FALLBACK_ZOOM = 1
const FALLBACK_CENTER = {
  lat: 49.81749199999999,
  lng: 15.472962000000052
}

class MapView extends View {
  constructor () {
    super(...arguments)

    const minScreen = BREAKPOINTS[this.$el.data('min-screen')]
    if (minScreen && window.innerWidth < minScreen) {
      this.$el.remove()
      return
    }

    this.emit('map:loading')

    this.zoom = this.$el.data('map-zoom') || DEFAULT_ZOOM
    this.markersData = {}

    const markerPath = this.$el.data('markerPath')

    const adjustToCoordinates = !!this.$el.data('adjust')
    const styles = THEMES[this.$el.data('style')] || []
    const options = { ...MAP_OPTIONS, markerPath, styles }
    const locations = this.$el.data('locations')

    this.map = new GoogleMap(this.el, {}, options, () => {
      this.resetMap()
      this.setMarkers(locations)

      if (adjustToCoordinates) {
        this.adjustToCoordinates(locations)
      }

      this.listen('map:reset', () => this.resetMap())
      this.listen('map:zoom_out', ({ body: value }) => this.zoomOut(value))
      this.listen('map:set_bounds', ({ body: {bounds, padding} }) => this.setBounds({bounds, padding}))
      this.listen('map:set_markers', ({ body: locations, options }) => this.setMarkers(locations, options))
      this.listen('map:activate_marker', ({ body: location }) => this.activateMarker(location))
      this.listen('map:deactivate_marker', ({ body: location }) => this.deactivateMarker(location))

      this.map.getGoogleMap().addListener('dragend', this.publishInteractionEvent.bind(this))
      this.map.getGoogleMap().addListener('zoom_changed', this.publishInteractionEvent.bind(this))

      this.emit('map:ready')
    })
  }

  setMarkers (locations, options = {}) {
    this._removeMarkers()
    this.map.geocodeLocations(locations).then(coordinates => {
      // Add markers
      coordinates.forEach((latLng, index) => {
        const { address, title, link } = locations[index]
        const indexKey = latLng.toString()

        if (!this.markersData[indexKey]) {
          const marker = this.map.addMarker(latLng, null, MARKER_WIDTH, MARKER_HEIGHT)
          const markerData = { marker, address, title, link }

          if (link) marker.addListener('click', () => this._navigateToMarker(markerData))
          marker.addListener('mouseover', () => this._activateMarker(markerData))
          marker.addListener('mouseout', () => this._deactivateMarker(markerData))

          marker.setTitle(`Location #${index + 1}`)

          this.markersData[indexKey] = markerData
        }
      })
    })
  }

  adjustToCoordinates (locations, options = {}) {
    this.map.geocodeLocations(locations).then(coordinates => {
      if (coordinates.length === 0) {
        this.map.setZoom(FALLBACK_ZOOM)
        this.map.setCenter(FALLBACK_CENTER)
      } else if (coordinates.length === 1) {
        this.map.setZoom(this.zoom)
        this.map.setCenter(coordinates[0])
      } else {
        this.map.fitCoords(coordinates)
      }
    })
  }

  activateMarker (location) {
    this.map.geocodeLocation(location).then(coordinates => {
      if (!coordinates) return
      const markerData = this.markersData[coordinates.toString()]
      if (markerData) this._activateMarker(markerData)
    })
  }

  deactivateMarker (location) {
    this.map.geocodeLocation(location).then(coordinates => {
      if (!coordinates) return
      const markerData = this.markersData[coordinates.toString()]
      if (markerData) this._deactivateMarker(markerData)
    })
  }

  setBounds ({bounds, padding}) {
    this.isLocked = true
    this.map.getGoogleMap().fitBounds(bounds, padding)
    this._runWhenReady(() => {
      this.isLocked = false
    })
  }

  zoomOut (value) {
    const { zoom } = this.map.getViewportData()
    let newZoom = zoom - (value || 1)
    if (newZoom < FALLBACK_ZOOM) newZoom = FALLBACK_ZOOM

    this.map.setZoom(newZoom)
  }

  resetMap () {
    this.isLocked = true
    this.map.setZoom(FALLBACK_ZOOM)
    this.map.setCenter(FALLBACK_CENTER)
    this._runWhenReady(() => {
      this.isLocked = false
    })
  }

  publishInteractionEvent () {
    if (this.isLocked) return
    this._runWhenReady(() => this.emit('map:interacted', this.map.getViewportData()))
  }

  _activateMarker ({ marker, address, title }) {
    marker.setZIndex(10)
    marker.setTitle(title || address)
  }

  _deactivateMarker (markerData) {
    markerData.marker.setZIndex(1)
    markerData.marker.setTitle(null)
  }

  _navigateToMarker (markerData) {
    window.location.href = markerData.link
  }

  _removeMarkers () {
    for (var indexKey in this.markersData) {
      this.markersData[indexKey].marker.setMap(null)
    }
    this.markersData = {}
  }

  // Google Map behavior is not deterministic, so when we call some method -
  // we need to wait until map starts idling. This means we can safely proceed.
  _runWhenReady (fn) {
    const listener = this.map.getGoogleMap().addListener('idle', () => {
      fn()
      window.google.maps.event.removeListener(listener)
    })
  }
}

module.exports = MapView



// WEBPACK FOOTER //
// ./front/apps/public/shared/components/ui/map/views/map.js